import { BookOpen, CalendarClock, Eye, EyeOff, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const coverSrc = (url) => url?.startsWith("/api/files/")
  ? `${process.env.REACT_APP_BACKEND_URL}${url}?auth=${localStorage.getItem("marsem_token")}`
  : (url || "/marsem-course-hero.png");

const accessValidity = (course) => {
  if (!course.access_expires_at) return "Dostęp bezterminowy";
  const date = new Date(course.access_valid_until ? `${course.access_valid_until}T12:00:00` : course.access_expires_at);
  const formatted = new Intl.DateTimeFormat("pl-PL", { day: "2-digit", month: "long", year: "numeric" }).format(date);
  if (course.access_expired) return `Dostęp wygasł ${formatted}`;
  const days = course.access_days_remaining;
  return `${days != null ? `${days} dni pozostało · ` : ""}dostęp do ${formatted}`;
};

function CourseCardContent({ course, student }) {
  const lessonCount = course.modules?.reduce((count, module) => count + (module.lessons?.length || 0), 0) || 0;
  return <><div className="course-image"><img src={coverSrc(course.cover_url)} alt={course.title} onError={(event) => { event.currentTarget.onerror=null; event.currentTarget.src="/marsem-course-hero.png"; }} data-testid={`course-cover-${course.id}`}/><span>{student && course.access_expired ? "DOSTĘP WYGASŁ" : course.status === "published" ? "OPUBLIKOWANY" : "SZKIC"}</span></div><div className="course-body"><small><BookOpen size={14}/> {lessonCount} LEKCJI{course.show_course_duration !== false ? ` · ${course.total_duration || "0 min"}` : ""}</small>{student ? <small className={`course-access-validity ${course.access_expired ? "expired" : ""}`}><CalendarClock /> {accessValidity(course)}</small> : null}<h3>{course.title}</h3><p>{course.subtitle}</p><div className="progress-line"><i style={{width:`${course.progress || 0}%`}}/><span>{course.progress || 0}%</span></div></div></>;
}

export default function CoursesPage() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => { api.get("/courses").then(({ data }) => setCourses(data)); }, []);

  const visible = courses.filter((course) => course.title.toLowerCase().includes(query.toLowerCase()));

  const togglePublication = async (course) => {
    const status = course.status === "published" ? "draft" : "published";
    setBusyId(course.id);
    try {
      await api.patch(`/courses/${course.id}/status`, { status });
      setCourses((current) => current.map((item) => item.id === course.id ? { ...item, status } : item));
      toast.success(status === "published" ? "Kurs opublikowany" : "Publikacja wyłączona");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Nie udało się zmienić publikacji");
    } finally { setBusyId(null); }
  };

  const removeCourse = async (course) => {
    if (!window.confirm(`Usunąć kurs „${course.title}”? Ta operacja usunie także dostępy i prace powiązane z kursem.`)) return;
    setBusyId(course.id);
    try {
      await api.delete(`/courses/${course.id}`);
      setCourses((current) => current.filter((item) => item.id !== course.id));
      toast.success("Kurs usunięty");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Nie udało się usunąć kursu");
    } finally { setBusyId(null); }
  };

  return <div className="page" data-testid="courses-page">
    <div className="page-heading"><div><span className="eyebrow">BIBLIOTEKA WIEDZY</span><h1 data-testid="courses-title">{user.role === "admin" ? "Produkty i kursy" : user.role === "curator" ? "Prowadzone kursy" : "Moje kursy"}</h1><p>{user.role === "admin" ? "Buduj, publikuj i zarządzaj kompletnymi doświadczeniami edukacyjnymi." : "Wybierz program i wróć do nauki."}</p></div>{user.role === "admin" ? <Link to="/admin/kursy/nowy" className="primary-btn compact" data-testid="new-course-button"><Plus size={18}/> NOWY KURS</Link> : null}</div>
    <div className="toolbar"><div className="search"><Search/><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Szukaj kursu…" data-testid="course-search-input"/></div><span data-testid="course-count">{visible.length} PROGRAMÓW</span></div>
    <div className="course-grid large">{visible.map((course) => <article className={`course-card ${course.access_expired ? "access-expired" : ""}`} key={course.id} data-testid={`course-card-${course.id}`}>
      {user.role === "student" && course.access_expired ? <div className="course-card-main expired" aria-disabled="true" data-testid={`expired-course-${course.id}`}><CourseCardContent course={course} student/></div> : <Link className="course-card-main" to={user.role === "admin" ? `/admin/kursy/${course.id}` : `/kursy/${course.id}`}><CourseCardContent course={course} student={user.role === "student"}/></Link>}
      {user.role === "admin" ? <div className="course-admin-actions"><Link to={`/admin/kursy/${course.id}`}><Pencil/> Edytuj</Link><button disabled={busyId===course.id} onClick={() => togglePublication(course)}>{course.status === "published" ? <EyeOff/> : <Eye/>}{course.status === "published" ? "Wyłącz" : "Opublikuj"}</button><button className="danger" disabled={busyId===course.id} onClick={() => removeCourse(course)}><Trash2/> Usuń</button></div> : null}
    </article>)}</div>
  </div>;
}
